import React from 'react';
import Form from 'components/form';
import Switch from 'components/switch';
import NodeTextEditor from 'routes/surface/components/node-modal-form/components/node-text-editor';
import If from 'components/if-statement';
import useCanCreateOrEditSurfaceNode from 'routes/surface/components/node-modal-form/hooks/use-can-create-or-edit-surface-node';
import { useSurfaceNodeTextSnippetsOperations } from 'routes/surface/hooks/use-surface-node-text-snippets';
import { useTranslation } from 'i18n';
import { Row, WithMargin } from 'components/layout';
import {
  Header,
  NameEditor,
  EditorWrapper,
  SimpleBar,
  DisabledName,
} from 'routes/surface/components/node-modal-form/style';
import { object, string } from 'yup';
import { NodeModalInput } from 'types';
import { isMobile } from 'react-device-detect';
import { SurfaceNoteNode } from 'graphql/types';

export interface NoteNodeFormProps {
  onSubmit: Function;
  closeForm: Function;
  initialValues: NodeModalInput<SurfaceNoteNode>;
}

const defaultProps = {};

function createValidationSchema(t) {
  return function () {
    return object().shape({
      name: string().required(t('name is required')),
    });
  };
}

function NoteNodeForm({ initialValues, ...props }: NoteNodeFormProps) {
  const { t } = useTranslation();

  const [
    allGlobalLinkIndicatorsVisible,
    setAllGlobalLinkIndicatorsVisible,
  ] = React.useState(false);

  const editorWrapper = React.useRef<HTMLElement>();

  const validationSchema = React.useMemo(createValidationSchema(t), []);

  const {
    canCreateOrEdit,
    isUpdate,
    cannotEdit,
  } = useCanCreateOrEditSurfaceNode(initialValues);

  const submitLabel = isUpdate ? t('Update note') : t('Insert note');

  const {
    removeDeletedSnippetFromGlobalLinkZone,
  } = useSurfaceNodeTextSnippetsOperations();

  function isNameInputAutoFocused() {
    if (isUpdate) {
      return isMobile ? false : true;
    }

    return true;
  }

  function toggleGlobalLinkIndicatorVisibility() {
    setAllGlobalLinkIndicatorsVisible(!allGlobalLinkIndicatorsVisible);
  }

  function onSubmit(values, actions, { closeTheForm = true } = {}) {
    return props.onSubmit(values, actions).then((response) => {
      actions.setSubmitting(false);

      if (response.isSuccess) {
        removeDeletedSnippetFromGlobalLinkZone(values.snippets);

        closeTheForm && props.closeForm();
      }

      return response;
    });
  }

  return (
    <Form
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit as any}>
      {(form) => (
        <>
          <Header>
            <Form.Button
              label={t('Close')}
              onClick={props.closeForm}
              secondary
            />
            <Row alignCenter>
              {isUpdate && (
                <WithMargin right="16px">
                  <Switch
                    label={t('Show all links')}
                    onChange={toggleGlobalLinkIndicatorVisibility}
                    checked={allGlobalLinkIndicatorsVisible}
                  />
                </WithMargin>
              )}
              {canCreateOrEdit && (
                <Form.SubmitButton
                  label={submitLabel}
                  onClick={form.handleSubmit}
                  tertiary
                />
              )}
            </Row>
          </Header>
          <EditorWrapper ref={editorWrapper}>
            <SimpleBar>
              <If condition={cannotEdit}>
                <If.Then>
                  <DisabledName>{form.values.name}</DisabledName>
                </If.Then>
                <If.Else>
                  <NameEditor
                    name="name"
                    placeholder={t('Note name')}
                    autoFocus={isNameInputAutoFocused()}
                  />
                </If.Else>
              </If>

              <NodeTextEditor
                name="slateData"
                snippets={initialValues.snippets}
                closeForm={props.closeForm}
                onSubmit={onSubmit}
                allGlobalLinkIndicatorsVisible={allGlobalLinkIndicatorsVisible}
                readOnly={cannotEdit}
                isNote
              />
            </SimpleBar>
          </EditorWrapper>
        </>
      )}
    </Form>
  );
}

NoteNodeForm.defaultProps = defaultProps;

export default NoteNodeForm;
